import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { iconBell } from '@sorare/core/src/atoms/icons/system/iconBell.ts';
import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { ANY_SPORT_PLAYERS_SLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/lib/gql';

import { DumbNotification } from '#src/components/activity/DumbNotification/index.tsx';
import { useAuctionLink } from '#src/components/activity/Notification/AuctionNotification/useAuctionLink.ts';
import { commonNotificationInterfaceFragment } from '#src/components/activity/Notification/fragments.ts';
import type { CommonNotificationProps } from '#src/components/activity/Notification/types.ts';

import type {
  ReminderNotification_auctionReminderNotification,
  TokenAuction_auctionReminderNotification,
} from './__generated__/index.graphql.ts';

type Props = CommonNotificationProps & {
  notification: ReminderNotification_auctionReminderNotification;
};

const useLinkForReminderNotification = () => {
  const { generateSportContextPath } = useSportContext();
  const makeLink = useAuctionLink();

  return (notification: ReminderNotification_auctionReminderNotification) => {
    const { name, remindable, similar } = notification;
    if (!isType(remindable, 'TokenAuction')) {
      return undefined;
    }
    if (name === 'auction_missed') {
      if (remindable.cards.length === 1) {
        const { sport, anyPlayer, rarityTyped, seasonYear } =
          remindable.cards[0];
        return generateSportContextPath(
          `${ANY_SPORT_PLAYERS_SLUG_CARDS}?sale=true&rarity=${rarityTyped}&season=${seasonYear}`,
          {
            params: {
              slug: anyPlayer.slug,
            },
            sport,
          }
        );
      }
      return makeLink(remindable);
    }
    const ofInterest = similar || remindable;
    if (isType(ofInterest, 'TokenAuction')) {
      return makeLink(ofInterest);
    }
    return undefined;
  };
};

export const AuctionReminderNotification = ({
  notification,
  ...rest
}: Props) => {
  const { createdAt, name, read, sport } = notification;
  const makeLink = useLinkForReminderNotification();

  const link = makeLink(notification);

  return (
    <DumbNotification
      name={name}
      title={<LazyMarkdown data={notification.markdownBody} />}
      link={link}
      createdAt={createdAt}
      read={read}
      sport={sport}
      icon={iconBell}
      {...rest}
    />
  );
};

const auctionOfInterest = gql`
  fragment TokenAuction_auctionReminderNotification on TokenAuction {
    id
    cards: anyCards {
      slug
      sport
      pictureUrl
    }
  }
` as TypedDocumentNode<TokenAuction_auctionReminderNotification>;

AuctionReminderNotification.fragments = {
  auctionReminderNotification: gql`
    fragment ReminderNotification_auctionReminderNotification on ReminderNotification {
      id
      ...Notification_notificationInterface
      remindable {
        ... on TokenAuction {
          id
          ...TokenAuction_auctionReminderNotification
          ...AuctionLink_auction
          cards: anyCards {
            slug
            anyPlayer {
              slug
            }
            rarityTyped
            seasonYear
          }
        }
      }
      similar {
        ... on TokenAuction {
          id
          ...TokenAuction_auctionReminderNotification
          ...AuctionLink_auction
        }
      }
    }
    ${commonNotificationInterfaceFragment}
    ${useAuctionLink.fragments.auctionLink}
    ${auctionOfInterest}
  ` as TypedDocumentNode<ReminderNotification_auctionReminderNotification>,
};
